import AppButton from '@nxsys/components/AppButton';
import * as Icons from '@nxsys/components/AppIconSvg';
import AppCard from '@nxsys/components/AppCard';
import AppPageTitle from '@nxsys/components/AppPageTitle';
import AppsContainer from '@nxsys/components/AppsContainer';
import AppsContent from '@nxsys/components/AppsContainer/AppsContent';
import AppTableContainer from '@nxsys/components/AppTableContainer';
import { useTypedIntl } from '@nxsys/hooks/useTypedIntl';
import { AppDispatch, AppState } from '@nxsys/store';
import { exportActions } from '@nxsys/store/slices/export/reducer/exportSlice';
import {
  EXPORT_PERIOD_MODE,
  EXPORT_STATUS,
  ExportCompanyParams,
  GetExportListParams,
  IExport,
} from '@nxsys/store/slices/export/types';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { useDispatch } from 'react-redux';
import { getColumns } from './columns';
import ExportCompanyModal from '../components/ExportCompanyModal';
import { toNumber } from 'lodash';
import { FILE_TYPE_VALUE } from '@nxsys/utils';
import { showMessage } from '@nxsys/utils/message';
import { saveFile } from '@nxsys/utils/save-file';

const ExportListing = () => {
  const { messages } = useTypedIntl();

  const dispatch = useDispatch<AppDispatch>();
  const { exportList } = useSelector((state: AppState) => state.exportStore);
  const { loading, list, pagination, queryParams } = exportList;

  // NX-5603: the exports this page asked for. Each is saved, or reported as
  // failed, once it shows up finished. Lost on a refresh or navigation; the
  // row and the notification still lead to the file.
  const awaitedExports = useRef(new Set<number>());

  const settleAwaitedExports = (rows: IExport[] | undefined) => {
    rows?.forEach((row) => {
      if (!awaitedExports.current.has(row.id)) return;
      if (row.status === EXPORT_STATUS.COMPLETED && row.linkFile) {
        awaitedExports.current.delete(row.id);
        saveFile(row.linkFile);
        showMessage({
          type: 'success',
          content: 'Your files have been downloaded successfully',
        });
      } else if (row.status === EXPORT_STATUS.FAILED) {
        awaitedExports.current.delete(row.id);
        showMessage({
          type: 'error',
          content: 'Your export could not be prepared. Please try again.',
        });
      }
    });
  };

  const { notificationList } = useSelector(
    (state: AppState) => state.notificationStore,
  );

  useEffect(() => {
    const notificationType = notificationList?.data?.[0]?.notificationType;
    if (
      notificationType === 'EXPORT_COMPANY_DATA' ||
      notificationType === 'EXPORT_COMPANY_DATA_FAILED'
    ) {
      // A company export can take tens of minutes, so refetch whenever one
      // finishes while the page is open, to update its status.
      refetchData();
      // The history lists the latest update first, so an export that finishes
      // moves to the top of the first page. While another page is shown, look
      // for the awaited exports there, without changing the page.
      if (awaitedExports.current.size && queryParams?.page) {
        findAwaitedExports();
      }
    }
  }, [notificationList]);

  useEffect(() => {
    settleAwaitedExports(list);
  }, [list]);

  const [exportCompanyModal, setExportCompanyModal] = useState({
    open: false,
  });
  const [isExporting, setIsExporting] = useState<boolean>(false);

  useEffect(() => {
    handleFetchData({
      page: 0,
      size: 20,
    });
  }, []);

  const handleFetchData = (params: GetExportListParams) => {
    dispatch(exportActions.getExportList(params));
  };

  const refetchData = () => handleFetchData({ ...queryParams });

  const findAwaitedExports = async () => {
    const result = await dispatch(
      exportActions.findExports({ ...queryParams, page: 0 }),
    );
    if (exportActions.findExports.fulfilled.match(result)) {
      settleAwaitedExports(result.payload);
    }
  };

  const renderTitle = useCallback(() => {
    return (
      <AppPageTitle prefix={messages['export.title']} separator="" title={''} />
    );
  }, [messages]);

  const renderRightContent = () => {
    return (
      <div className="flex items-center gap-2">
        <AppButton
          type="primary"
          name={messages['common.export']}
          leftIcon={<Icons.IconArrowExport color="#fff" />}
          onClick={toggleExportCompany}
        />
      </div>
    );
  };

  const onChangePagination = (page: number, pageSize: number) => {
    const isPageSizeChanged = pageSize !== pagination.pageSize;
    const shouldResetPage = isPageSizeChanged && pagination.current > 0;
    handleFetchData({
      ...queryParams,
      page: shouldResetPage ? 0 : page - 1,
      size: pageSize,
    });
  };

  const toggleExportCompany = () =>
    setExportCompanyModal({
      ...exportCompanyModal,
      open: !exportCompanyModal.open,
    });

  // Only the fields of the mode picked are sent. "All Tax Periods", and the
  // file types that pick no period, send none of them, which the BE reads as
  // every period of the chosen tax year.
  const buildPeriodParams = (
    values: ExportCompanyParams,
  ): Pick<
    ExportCompanyParams,
    'taxPeriods' | 'paymentStartDate' | 'paymentEndDate'
  > => {
    switch (values.periodMode) {
      case EXPORT_PERIOD_MODE.SPECIFIC:
        return { taxPeriods: (values.taxPeriods ?? []).map(toNumber) };
      case EXPORT_PERIOD_MODE.CUSTOM_RANGE: {
        const [from, to] = values.payDates ?? [];
        return {
          paymentStartDate: from?.format('YYYY-MM-DD'),
          paymentEndDate: to?.format('YYYY-MM-DD'),
        };
      }
      default:
        return {};
    }
  };

  const handleExport = async (values: ExportCompanyParams) => {
    const params: ExportCompanyParams = {
      ids: values.ids,
      fileType: values.fileType,
      ...(values.fileType !== FILE_TYPE_VALUE.SUBCONTRACTOR
        ? {
            ...buildPeriodParams(values),
            taxYear: toNumber(values.taxYear),
          }
        : {}),
    };
    setIsExporting(true);
    const result = await dispatch(exportActions.exportCompany(params));
    setIsExporting(false);
    const exportId = exportActions.exportCompany.fulfilled.match(result)
      ? result.payload.data?.exportId
      : undefined;
    if (exportId) awaitedExports.current.add(exportId);
    // Brings up the row of the new export, in progress (or failed, if it could
    // not be queued).
    handleFetchData({ ...queryParams, page: 0 });
  };

  return (
    <AppsContainer
      fullView
      type="bottom"
      title={renderTitle()}
      rightContent={renderRightContent()}
      loading={isExporting}
    >
      <AppCard>
        <AppsContent>
          <AppTableContainer
            data={list ?? []}
            loading={loading}
            page={pagination.current}
            total={pagination.totalDocs}
            pageSize={pagination.pageSize}
            scroll={{ x: 1200, y: 700 }}
            titleEmpty={messages['export.empty']}
            columns={getColumns({
              messages,
            })}
            onChangePagination={onChangePagination}
            noDataType="noDataCreated"
            addNewBtnName={messages['common.export']}
            addNewBtnOnClick={toggleExportCompany}
            addNewIcon={null}
          />
        </AppsContent>
      </AppCard>
      <ExportCompanyModal
        isOpen={exportCompanyModal.open}
        toggle={toggleExportCompany}
        onExport={handleExport}
      />
    </AppsContainer>
  );
};

export default ExportListing;
